import type { ITextObj } from '@team-milva/lab-models/textObj'

/**
 * The language a report description will actually be printed in. The report prints
 * `getTextObjValue(value, reportLocale)` (lab-models): the report language, else English, else the
 * first language that has any text. These helpers mirror that chain so the descriptions modal can
 * say, before anything is printed, when a report will not be in its own language.
 */

/** The locale `getTextObjValue(textObj, locale)` reads from; null when there is no text at all. */
export function getTextObjSourceLocale(textObj: ITextObj, locale: string): string | null {
  if (textObj[locale]) return locale
  if (textObj['en']) return 'en'
  return Object.keys(textObj).find((key) => textObj[key]) ?? null
}

/** The name of a language in another — `getLanguageName('fr', 'da')` is "fransk". */
export function getLanguageName(locale: string, inLocale: string): string {
  try {
    return new Intl.DisplayNames([inLocale], { type: 'language' }).of(locale) ?? locale
  } catch {
    return locale
  }
}

/** Same, with a capital first letter — for a label that starts with the name. */
export function getCapitalizedLanguageName(locale: string, inLocale: string): string {
  const name = getLanguageName(locale, inLocale)
  return name.charAt(0).toLocaleUpperCase(inLocale) + name.slice(1)
}

export interface IReportLocaleIssue {
  /** What is missing in the report language. */
  missing: 'title' | 'text' | 'both'
  /** The language the report will print instead. */
  fallbackLocale: string
}

/**
 * Whether a description item will be printed in another language than the report's. Only counts as
 * missing when the item has the part in some other language — an item that simply has no text at
 * all is a different matter and not a translation gap.
 */
export function getReportLocaleIssue(
  item: { title: ITextObj; text: ITextObj },
  reportLocale: string
): IReportLocaleIssue | null {
  const titleSource = getTextObjSourceLocale(item.title, reportLocale)
  const textSource = getTextObjSourceLocale(item.text, reportLocale)
  const isTitleMissing = titleSource !== null && titleSource !== reportLocale
  const isTextMissing = textSource !== null && textSource !== reportLocale
  if (!isTitleMissing && !isTextMissing) return null
  const fallbackLocale = (isTextMissing ? textSource : titleSource) as string
  return {
    missing: isTitleMissing && isTextMissing ? 'both' : isTextMissing ? 'text' : 'title',
    fallbackLocale,
  }
}
